/* The at-a-glance "how is this combatant doing?" readout, in four states.
 *
 * ONE DERIVATION, TWO SURFACES. The party overview cards and the initiative
 * rows both show this, so the rule lives here and the component only paints it.
 * A character who reads "Bloodied" on the card must read "Bloodied" on the
 * tracker; a second copy of the thresholds would drift the first time one moved.
 *
 * ABSENT IS NOT ZERO (the trap this whole repo keeps stepping in). A character
 * mid-import, hand-made before any HP is entered, or with a blank maximum has an
 * UNKNOWN state, not a healthy or a dead one. Number("") and Number(null) are
 * both 0 and finite, so a naive "is it a number?" guard reads a blank as a real
 * 0 and paints a live party "Dead". The derivation returns null for unknown and
 * the badge then renders nothing, rather than inventing a state.
 *
 * NPCs are different and are not derived here: a creature has no death saves and
 * no DM-Screen HP field — its hit points live on the Roll20 token bar — so the
 * initiative row maps monsterVitals' hpState() through healthStateFromHp() below.
 */
import type { Character } from "./types";
import type { HpState } from "./monsterVitals";

export type HealthState = "healthy" | "bloodied" | "unconscious" | "dead";

/** Label + hover text per state, so the badge reads the same everywhere. */
export const HEALTH_META: Record<HealthState, { label: string; title: string }> = {
  healthy: { label: "Healthy", title: "Above the bloodied threshold." },
  bloodied: { label: "Bloodied", title: "At or below half its hit points — half or more of its health is gone." },
  unconscious: { label: "Unconscious", title: "Down at 0 hit points and making death saves. Not dead yet: a heal or a stabilise brings them back." },
  dead: { label: "Dead", title: "Three failed death saves." },
};

/** D&D 5e: a player character is dead at three failed death saves. */
export const DEATH_SAVE_FAILURES_TO_DIE = 3;

/* The four-state health of a player character, from current HP vs maximum plus
 * the death-save state the app already tracks. Returns null when HP or the
 * maximum is unknown — see "ABSENT IS NOT ZERO" above.
 *
 * The branch order carries the one subtle case: a character at 0 HP is "down",
 * and whether they read Unconscious or Dead turns on their failed death saves,
 * so "down" is split by that count rather than tested after a flat "<= 0". */
export function healthStateOf(c: Pick<Character, "hp" | "maxHp" | "deathFailures">): HealthState | null {
  // Absent is not zero. A null, a blank string, or a non-finite value is
  // UNKNOWN, never a real 0 — the same guard the tracker's isUnconscious uses,
  // so a mid-import blank HP cannot paint a live character "Unconscious".
  const known = (v: unknown) => v != null && String(v).trim() !== "" && Number.isFinite(Number(v));
  if (!known(c.hp) || !known(c.maxHp)) return null;
  const hp = Number(c.hp), maxHp = Number(c.maxHp);
  if (maxHp <= 0) return null;                        // no honest percentage without a real maximum
  // Down at 0 or below: three failed death saves is dead; otherwise unconscious.
  if (hp <= 0) return Number(c.deathFailures) >= DEATH_SAVE_FAILURES_TO_DIE ? "dead" : "unconscious";
  // Still standing: bloodied at or below half its maximum (5e's convention), else healthy.
  return hp <= maxHp / 2 ? "bloodied" : "healthy";
}

/* A creature's state from its token-bar vitals. NPCs don't fall unconscious or
 * roll death saves — at 0 HP they are simply out of the fight — so "down" maps
 * to "dead", and an unread bar ("unknown") shows no badge. */
export function healthStateFromHp(s: HpState): HealthState | null {
  switch (s) {
    case "ok": return "healthy";
    case "bloodied": return "bloodied";
    case "down": return "dead";
    default: return null;
  }
}
